import { resourceDetailsQueryPredicates } from '../queries/resourceDetailsQueryPredicates.js';
import { mutateNotepadContentV2 } from '../utils/mutateNotepadContent.js';
import { useQueryCacheResourceHelpers } from '../../resources/utils/useQueryCacheResourceHelpers.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Y from 'yjs';
import { useWoody } from '../../woody/woody.js';
import { useOptimisticallyUpdateQueriesWithPredicate } from '@fabric-app/queries/utilities';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { useRef, useMemo } from 'react';
import { useIsFeatureFlagEnabled, FeatureFlag } from '../../feature-flags/feature-flags.store.js';
import { createLogger } from '@fabric-app/utils/logging';
import { sensitiveCall } from '@fabric-app/utils/promise';
import { NOTEPAD_VERSION } from '../../Tiptap/tiptap.config.js';

const useOptimisticNotepadStateUpdate = () => {
  const queryClient = useQueryClient();
  const { updateCachedResource: _updateCachedResource } = useQueryCacheResourceHelpers();
  const updateCachedResourceRef = useRef(_updateCachedResource);
  updateCachedResourceRef.current = _updateCachedResource;
  const _optimisticallyUpdateQueries = useOptimisticallyUpdateQueriesWithPredicate();
  const optimisticallyUpdateQueriesRef = useRef(_optimisticallyUpdateQueries);
  optimisticallyUpdateQueriesRef.current = _optimisticallyUpdateQueries;
  return useMemo(() => {
    return ({ resource, update, optimisticContent }) => {
      queryClient.cancelQueries({
        predicate: (q) => resourceDetailsQueryPredicates.notepadStateById(q, resource.id)
      });
      const optimisticallyUpdateState = optimisticallyUpdateQueriesRef.current((q) => resourceDetailsQueryPredicates.notepadStateById(q, resource.id), (data) => {
        return data ? Y.mergeUpdates([
          data,
          update
        ]) : update;
      });
      return {
        optimisticallyUpdateState,
        optimisticallyUpdateResource: optimisticContent ? updateCachedResourceRef.current({
          id: resource.id
        }, {
          resourceUpdateFn: (resource2) => {
            return mutateNotepadContentV2(resource2, optimisticContent);
          }
        }) : void 0
      };
    };
  }, [
    queryClient
  ]);
};
const mutationKeyNotepadStateUpdate = (resourceId) => {
  return [
    "notepadStateUpdate",
    resourceId
  ];
};
const logger = createLogger("useMutationNotepadStateUpdate");
const useMutationNotepadStateUpdate = (options) => {
  const { client } = useWoody();
  const optimisticNotepadStateUpdate = useOptimisticNotepadStateUpdate();
  const accessToken = useContextAccessToken();
  const useNewNotepadStateUpdate = useIsFeatureFlagEnabled(FeatureFlag.NOTEPAD_NEW_UPDATE_SERVICE);
  const mutation = useMutation({
    mutationKey: mutationKeyNotepadStateUpdate(options.resourceId),
    mutationFn: async ({ resource, update }) => {
      if (resource.isReadonly) {
        return;
      }
      await sensitiveCall(async () => {
        logger.logDevOnly("useMutationNotepadStateUpdate", {
          resourceId: resource.id,
          update
        });
        await client.v2({
          endpoint: "/v2/notepads/{resourceId}/state",
          params: {
            resourceId: resource.id
          }
        }, {
          query: {
            accessToken,
            ...useNewNotepadStateUpdate && {
              queue: "true"
            },
            version: NOTEPAD_VERSION
          },
          method: "patch",
          body: new Blob([
            update
          ], {
            type: "application/octet-stream"
          })
        });
      })();
    },
    onMutate: (vars) => {
      return optimisticNotepadStateUpdate(vars);
    },
    onError: (_, _v, context) => {
      context?.optimisticallyUpdateResource?.resetCacheToPreOptimisticState();
      context?.optimisticallyUpdateState?.resetQueriesData();
      context?.optimisticallyUpdateResource?.invalidateQueries();
      context?.optimisticallyUpdateState?.invalidateQueries();
    }
  });
  return mutation;
};

export { mutationKeyNotepadStateUpdate, useMutationNotepadStateUpdate, useOptimisticNotepadStateUpdate };
//# sourceMappingURL=useMutationNotepadStateUpdate.js.map
